import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Car, Package, Home, Building2, Upload, X, CheckCircle2, ArrowRight, ArrowLeft,
  Loader2, Truck, Warehouse, Ship, MapPin, Store, Info
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  { id: 'vehicule', title: 'Véhicule', subtitle: 'Voiture, moto, quad, scooter', icon: Car, color: 'from-blue-600 to-cyan-500', ring: 'ring-blue-500' },
  { id: 'palette', title: 'Palette / Groupage', subtitle: 'Colis volumineux, palettes isolées', icon: Package, color: 'from-emerald-600 to-teal-500', ring: 'ring-emerald-500' },
  { id: 'demenagement', title: 'Déménagement', subtitle: 'Conteneur complet 20\' / 40\'', icon: Home, color: 'from-purple-600 to-indigo-500', ring: 'ring-purple-500' },
  { id: 'grossiste', title: 'Fret grossiste', subtitle: 'E-commerçants, import/export', icon: Building2, color: 'from-orange-600 to-amber-500', ring: 'ring-orange-500' }
];

const PICKUP_METHODS = [
  { id: 'enlevement_domicile', title: 'Enlèvement à votre adresse', desc: 'On vient récupérer votre marchandise partout en France', icon: Truck, color: 'from-blue-500 to-cyan-500' },
  { id: 'depot_entrepot', title: 'Dépôt à notre entrepôt', desc: 'Vous apportez vous-même à notre entrepôt de Béziers (34500)', icon: Warehouse, color: 'from-emerald-500 to-teal-500' },
  { id: 'livraison_fournisseur', title: 'Livré par un fournisseur', desc: 'Votre vendeur (Amazon, concessionnaire...) livre directement chez nous', icon: Package, color: 'from-purple-500 to-indigo-500' }
];

const DELIVERY_METHODS = [
  { id: 'port_terminal', title: 'Remise au port / terminal', desc: 'Le destinataire retire directement au port à l\'arrivée', icon: Ship, color: 'from-cyan-500 to-blue-500' },
  { id: 'domicile_dest', title: 'Livraison à domicile', desc: 'Livraison jusqu\'à l\'adresse du destinataire', icon: Home, color: 'from-emerald-500 to-teal-500' },
  { id: 'entrepot_dest', title: 'Livraison à un entrepôt', desc: 'Vers l\'entrepôt d\'une société (B2B)', icon: Warehouse, color: 'from-purple-500 to-indigo-500' },
  { id: 'retrait_agence', title: 'Retrait en agence', desc: 'Le destinataire retire dans une agence partenaire', icon: Store, color: 'from-orange-500 to-amber-500' }
];

const POPULAR_DESTINATIONS = [
  'Guadeloupe', 'Martinique', 'La Réunion', 'Guyane', 'Mayotte',
  'Polynésie française', 'Nouvelle-Calédonie', 'Wallis-et-Futuna',
  'Saint-Barthélemy', 'Saint-Martin',
  'Maroc', 'Algérie', 'Tunisie',
  'Sénégal', 'Côte d\'Ivoire', 'Cameroun', 'Gabon', 'Mali', 'Bénin', 'Togo',
  'Madagascar', 'Maurice',
  'Canada', 'États-Unis',
  'Autre (préciser dans notes)'
];

const MAX_PHOTOS = 8;
const MAX_PHOTO_SIZE_MB = 5;

const INITIAL_FORM = {
  service_type: '',
  // Contact
  name: '', email: '', phone: '',
  is_company: false, company: '',
  // Pickup
  pickup_method: '',
  pickup_city: '', pickup_postal_code: '', pickup_address: '',
  pickup_contact_name: '', pickup_contact_phone: '',
  pickup_access_notes: '',
  // Delivery
  delivery_method: '',
  destination_country: '', destination_city: '', destination_address: '',
  recipient_name: '', recipient_phone: '',
  delivery_access_notes: '',
  desired_date: '',
  // Marchandise
  description: '',
  already_packed: false, needs_packaging: false,
  declared_value: '',
  has_customs_docs: false,
  frequency: 'ponctuel',
  // Vehicle
  vehicle_brand: '', vehicle_model: '', vehicle_year: '',
  vehicle_value: '', vehicle_running: true,
  // Palette
  nb_pallets: '', total_weight: '', total_volume: '', dimensions: '',
  // Déménagement
  container_size: 'groupage', volume_m3: '', needs_packing: false,
  // Extra
  notes: '', photos: []
};

export default function FretQuoteForm() {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const selectedService = SERVICES.find(s => s.id === form.service_type);

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (form.photos.length + files.length > MAX_PHOTOS) {
      toast.error(`Maximum ${MAX_PHOTOS} photos`);
      return;
    }
    const valid = [];
    for (const file of files) {
      if (file.size > MAX_PHOTO_SIZE_MB * 1024 * 1024) {
        toast.error(`${file.name} dépasse ${MAX_PHOTO_SIZE_MB} Mo`);
        continue;
      }
      const dataUrl = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      valid.push({ filename: file.name, data: dataUrl, size: file.size });
    }
    update('photos', [...form.photos, ...valid]);
    e.target.value = '';
  };

  const removePhoto = (idx) => update('photos', form.photos.filter((_, i) => i !== idx));

  const canGoStep2 = !!form.service_type;
  const canGoStep3 = form.pickup_method && form.pickup_city;
  const canGoStep4 = form.delivery_method && form.destination_country && form.description;
  const canSubmit = form.name && form.email && form.phone;

  const submit = async () => {
    if (!canSubmit) { toast.error('Merci de remplir les champs obligatoires'); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        vehicle_value: form.vehicle_value ? parseFloat(form.vehicle_value) : null,
        declared_value: form.declared_value ? parseFloat(form.declared_value) : null,
        nb_pallets: form.nb_pallets ? parseInt(form.nb_pallets) : null,
        total_weight: form.total_weight ? parseFloat(form.total_weight) : null,
        total_volume: form.total_volume ? parseFloat(form.total_volume) : null,
        volume_m3: form.volume_m3 ? parseFloat(form.volume_m3) : null
      };
      await axios.post(`${API}/fret/quote`, payload);
      setSuccess(true);
      toast.success('Demande envoyée ! Nous vous répondrons sous 24h.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <Card className="bg-white border-slate-200 shadow-xl" data-testid="fret-form-success">
        <CardContent className="p-10 text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-600" />
          </div>
          <h3 className="font-heading font-bold text-2xl text-slate-900 mb-3">Demande envoyée !</h3>
          <p className="text-slate-600 mb-2">Merci {form.name}, nous avons bien reçu votre demande.</p>
          <p className="text-slate-600 mb-8">
            Notre équipe vous enverra un devis personnalisé par email sous <strong>24h ouvrées</strong>.
          </p>
          <Button
            onClick={() => { setSuccess(false); setStep(1); setForm(INITIAL_FORM); }}
            variant="outline"
            data-testid="fret-form-new"
          >
            Faire une nouvelle demande
          </Button>
        </CardContent>
      </Card>
    );
  }

  const steps = [
    { n: 1, label: 'Service' },
    { n: 2, label: 'Enlèvement' },
    { n: 3, label: 'Livraison' },
    { n: 4, label: 'Contact' }
  ];

  return (
    <Card className="bg-white border-slate-200 shadow-xl" data-testid="fret-quote-form">
      <CardContent className="p-6 md:p-8">
        {/* Progress */}
        <div className="flex items-center justify-between mb-8">
          {steps.map((s, idx) => (
            <React.Fragment key={s.n}>
              <div className={`flex items-center gap-2 ${step >= s.n ? 'text-blue-600' : 'text-slate-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${step >= s.n ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
                  {s.n}
                </div>
                <span className="text-xs sm:text-sm font-medium hidden md:inline">{s.label}</span>
              </div>
              {idx < steps.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${step > s.n ? 'bg-blue-600' : 'bg-slate-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* STEP 1 : Service type */}
        {step === 1 && (
          <div>
            <h3 className="font-heading font-bold text-2xl text-slate-900 mb-2">Quel type d'envoi ?</h3>
            <p className="text-slate-600 mb-6">Sélectionnez le service qui correspond à votre besoin.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {SERVICES.map((s) => {
                const Icon = s.icon;
                const active = form.service_type === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => update('service_type', s.id)}
                    data-testid={`fret-service-${s.id}`}
                    className={`text-left p-5 rounded-xl border-2 transition-all ${active ? `border-transparent ring-2 ${s.ring} bg-slate-50` : 'border-slate-200 hover:border-slate-300'}`}
                  >
                    <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${s.color} flex items-center justify-center mb-3`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="font-semibold text-slate-900 mb-1">{s.title}</h4>
                    <p className="text-sm text-slate-500">{s.subtitle}</p>
                  </button>
                );
              })}
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setStep(2)} disabled={!canGoStep2} data-testid="fret-next-1" className="bg-blue-600 hover:bg-blue-700">
                Suivant <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 2 : Pickup (enlèvement en France) */}
        {step === 2 && (
          <div className="space-y-5">
            <div>
              <h3 className="font-heading font-bold text-2xl text-slate-900 mb-2">Enlèvement en France</h3>
              <p className="text-slate-600">Comment souhaitez-vous que nous récupérions votre marchandise ?</p>
            </div>

            <div className="grid grid-cols-1 gap-3">
              {PICKUP_METHODS.map((m) => {
                const Icon = m.icon;
                const active = form.pickup_method === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => update('pickup_method', m.id)}
                    data-testid={`fret-pickup-${m.id}`}
                    className={`flex items-start gap-4 text-left p-4 rounded-xl border-2 transition-all ${active ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
                  >
                    <div className={`w-11 h-11 rounded-lg bg-gradient-to-br ${m.color} flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">{m.title}</h4>
                      <p className="text-sm text-slate-600">{m.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              <div>
                <Label>Ville {form.pickup_method === 'depot_entrepot' ? '(dépôt)' : ''} *</Label>
                <Input
                  value={form.pickup_city}
                  onChange={e => update('pickup_city', e.target.value)}
                  placeholder={form.pickup_method === 'depot_entrepot' ? 'Béziers' : 'Paris, Marseille...'}
                  data-testid="fret-pickup-city"
                />
              </div>
              <div>
                <Label>Code postal</Label>
                <Input value={form.pickup_postal_code} onChange={e => update('pickup_postal_code', e.target.value)} placeholder="75001" />
              </div>

              {form.pickup_method === 'enlevement_domicile' && (
                <>
                  <div className="md:col-span-2">
                    <Label>Adresse complète d'enlèvement</Label>
                    <Input value={form.pickup_address} onChange={e => update('pickup_address', e.target.value)} placeholder="Rue, numéro, bâtiment..." />
                  </div>
                  <div>
                    <Label>Contact sur place (nom)</Label>
                    <Input value={form.pickup_contact_name} onChange={e => update('pickup_contact_name', e.target.value)} placeholder="Personne à contacter" />
                  </div>
                  <div>
                    <Label>Téléphone sur place</Label>
                    <Input type="tel" value={form.pickup_contact_phone} onChange={e => update('pickup_contact_phone', e.target.value)} placeholder="+33 6 XX XX XX XX" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Contraintes d'accès (facultatif)</Label>
                    <Textarea
                      value={form.pickup_access_notes}
                      onChange={e => update('pickup_access_notes', e.target.value)}
                      rows={2}
                      placeholder="Étage sans ascenseur, rue étroite, horaires, code portail..."
                    />
                  </div>
                </>
              )}
            </div>

            {form.pickup_method === 'depot_entrepot' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700">
                  <strong>Notre entrepôt :</strong> 105 Rue Joseph Marie Jacquard, 34500 Béziers, France.
                  Prenez rendez-vous avec nous avant de venir déposer votre marchandise.
                </p>
              </div>
            )}

            {form.pickup_method === 'livraison_fournisseur' && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex gap-3">
                <Info className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700">
                  Votre fournisseur doit livrer à : <strong>ISMPARCEL [votre nom] — 105 Rue Joseph Marie Jacquard, 34500 Béziers</strong>.
                  Indiquez votre nom dans les notes pour identifier le colis.
                </p>
              </div>
            )}

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="w-4 h-4 mr-2" /> Retour</Button>
              <Button onClick={() => setStep(3)} disabled={!canGoStep3} data-testid="fret-next-2" className="bg-blue-600 hover:bg-blue-700">
                Suivant <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3 : Delivery + marchandise */}
        {step === 3 && (
          <div className="space-y-5">
            <div>
              <h3 className="font-heading font-bold text-2xl text-slate-900 mb-2">Livraison à destination</h3>
              <p className="text-slate-600">Comment la marchandise doit-elle être remise au destinataire ?</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {DELIVERY_METHODS.map((m) => {
                const Icon = m.icon;
                const active = form.delivery_method === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => update('delivery_method', m.id)}
                    data-testid={`fret-delivery-${m.id}`}
                    className={`flex items-start gap-3 text-left p-4 rounded-xl border-2 transition-all ${active ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
                  >
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${m.color} flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-semibold text-slate-900 text-sm">{m.title}</h4>
                      <p className="text-xs text-slate-600">{m.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Destination */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              <div>
                <Label>Pays de destination *</Label>
                <select
                  value={form.destination_country}
                  onChange={e => update('destination_country', e.target.value)}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                  data-testid="fret-destination-country"
                >
                  <option value="">Choisir...</option>
                  {POPULAR_DESTINATIONS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <Label>Ville de destination</Label>
                <Input value={form.destination_city} onChange={e => update('destination_city', e.target.value)} placeholder="Ex: Papeete, Abidjan..." />
              </div>

              {(form.delivery_method === 'domicile_dest' || form.delivery_method === 'entrepot_dest') && (
                <>
                  <div className="md:col-span-2">
                    <Label>Adresse de livraison</Label>
                    <Input value={form.destination_address} onChange={e => update('destination_address', e.target.value)} placeholder="Rue, numéro, quartier..." />
                  </div>
                  <div>
                    <Label>Nom du destinataire</Label>
                    <Input value={form.recipient_name} onChange={e => update('recipient_name', e.target.value)} placeholder="Personne à livrer" />
                  </div>
                  <div>
                    <Label>Téléphone du destinataire</Label>
                    <Input type="tel" value={form.recipient_phone} onChange={e => update('recipient_phone', e.target.value)} placeholder="+XXX XX XX XX XX" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Contraintes d'accès livraison (facultatif)</Label>
                    <Textarea
                      value={form.delivery_access_notes}
                      onChange={e => update('delivery_access_notes', e.target.value)}
                      rows={2}
                      placeholder="Étage, accès véhicule, horaires..."
                    />
                  </div>
                </>
              )}

              <div>
                <Label>Date souhaitée (indicative)</Label>
                <Input type="date" value={form.desired_date} onChange={e => update('desired_date', e.target.value)} />
              </div>
              <div>
                <Label>Fréquence</Label>
                <select
                  value={form.frequency}
                  onChange={e => update('frequency', e.target.value)}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="ponctuel">Ponctuel (une seule fois)</option>
                  <option value="recurrent_mensuel">Récurrent mensuel</option>
                  <option value="recurrent_hebdo">Récurrent hebdomadaire</option>
                </select>
              </div>
            </div>

            {/* Marchandise specifics */}
            <div className="pt-2">
              <h4 className="font-semibold text-slate-900 mb-3">Détails de la marchandise</h4>

              {form.service_type === 'vehicule' && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 space-y-3 mb-4">
                  <h5 className="font-semibold text-slate-900 text-sm">Infos véhicule</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input placeholder="Marque (ex: Renault)" value={form.vehicle_brand} onChange={e => update('vehicle_brand', e.target.value)} />
                    <Input placeholder="Modèle (ex: Clio)" value={form.vehicle_model} onChange={e => update('vehicle_model', e.target.value)} />
                    <Input placeholder="Année (ex: 2020)" value={form.vehicle_year} onChange={e => update('vehicle_year', e.target.value)} />
                    <Input type="number" placeholder="Valeur déclarée (€)" value={form.vehicle_value} onChange={e => update('vehicle_value', e.target.value)} />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={form.vehicle_running} onChange={e => update('vehicle_running', e.target.checked)} />
                    Le véhicule est roulant
                  </label>
                </div>
              )}

              {form.service_type === 'palette' && (
                <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4 space-y-3 mb-4">
                  <h5 className="font-semibold text-slate-900 text-sm">Infos palette / marchandise</h5>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Input type="number" placeholder="Nb palettes" value={form.nb_pallets} onChange={e => update('nb_pallets', e.target.value)} />
                    <Input type="number" step="0.1" placeholder="Poids total (kg)" value={form.total_weight} onChange={e => update('total_weight', e.target.value)} />
                    <Input type="number" step="0.1" placeholder="Volume (m³)" value={form.total_volume} onChange={e => update('total_volume', e.target.value)} />
                  </div>
                  <Input placeholder="Dimensions (ex: 120x80x150 cm)" value={form.dimensions} onChange={e => update('dimensions', e.target.value)} />
                </div>
              )}

              {form.service_type === 'demenagement' && (
                <div className="bg-purple-50 border border-purple-100 rounded-lg p-4 space-y-3 mb-4">
                  <h5 className="font-semibold text-slate-900 text-sm">Infos déménagement</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <select value={form.container_size} onChange={e => update('container_size', e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                      <option value="groupage">Groupage (quelques m³)</option>
                      <option value="20ft">Conteneur 20' (~33 m³)</option>
                      <option value="40ft">Conteneur 40' (~67 m³)</option>
                      <option value="40hc">Conteneur 40' HC (~76 m³)</option>
                    </select>
                    <Input type="number" step="0.1" placeholder="Volume estimé (m³)" value={form.volume_m3} onChange={e => update('volume_m3', e.target.value)} />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={form.needs_packing} onChange={e => update('needs_packing', e.target.checked)} />
                    J'ai besoin du service d'emballage
                  </label>
                </div>
              )}

              {form.service_type === 'grossiste' && (
                <div className="bg-orange-50 border border-orange-100 rounded-lg p-4 space-y-3 mb-4">
                  <h5 className="font-semibold text-slate-900 text-sm">Infos fret commercial</h5>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Input type="number" placeholder="Nb palettes / colis" value={form.nb_pallets} onChange={e => update('nb_pallets', e.target.value)} />
                    <Input type="number" step="0.1" placeholder="Poids total (kg)" value={form.total_weight} onChange={e => update('total_weight', e.target.value)} />
                    <Input type="number" step="0.1" placeholder="Volume (m³)" value={form.total_volume} onChange={e => update('total_volume', e.target.value)} />
                  </div>
                </div>
              )}

              <div className="mb-3">
                <Label>Description de la marchandise *</Label>
                <Textarea
                  value={form.description}
                  onChange={e => update('description', e.target.value)}
                  placeholder="Décrivez précisément ce que vous souhaitez envoyer..."
                  rows={3}
                  data-testid="fret-description"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <div>
                  <Label>Valeur totale déclarée (€) <span className="text-xs text-slate-500">— pour l'assurance</span></Label>
                  <Input type="number" value={form.declared_value} onChange={e => update('declared_value', e.target.value)} placeholder="Ex: 5000" />
                </div>
                <div className="flex flex-col gap-2 pt-6">
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={form.already_packed} onChange={e => update('already_packed', e.target.checked)} />
                    Marchandise déjà emballée
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={form.needs_packaging} onChange={e => update('needs_packaging', e.target.checked)} />
                    J'ai besoin d'un service d'emballage
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={form.has_customs_docs} onChange={e => update('has_customs_docs', e.target.checked)} />
                    J'ai les documents douaniers (factures, certificats...)
                  </label>
                </div>
              </div>

              {/* Photos */}
              <div>
                <Label>Photos (facultatif, max {MAX_PHOTOS})</Label>
                <label className="flex items-center justify-center gap-2 w-full py-4 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-slate-50 transition-colors">
                  <Upload className="w-5 h-5 text-slate-500" />
                  <span className="text-sm text-slate-600">Ajouter des photos (max {MAX_PHOTO_SIZE_MB} Mo chacune)</span>
                  <input type="file" accept="image/*" multiple onChange={handlePhotos} className="hidden" data-testid="fret-photos-input" />
                </label>
                {form.photos.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-3">
                    {form.photos.map((p, idx) => (
                      <div key={idx} className="relative group">
                        <img src={p.data} alt={p.filename} className="w-full h-20 object-cover rounded-lg border border-slate-200" />
                        <button type="button" onClick={() => removePhoto(idx)} className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(2)}><ArrowLeft className="w-4 h-4 mr-2" /> Retour</Button>
              <Button onClick={() => setStep(4)} disabled={!canGoStep4} data-testid="fret-next-3" className="bg-blue-600 hover:bg-blue-700">
                Suivant <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 4 : Contact */}
        {step === 4 && (
          <div className="space-y-5">
            <div>
              <h3 className="font-heading font-bold text-2xl text-slate-900 mb-2">Vos coordonnées</h3>
              <p className="text-slate-600">Nous vous enverrons un devis personnalisé sous 24h ouvrées.</p>
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.is_company} onChange={e => update('is_company', e.target.checked)} />
              Je représente une entreprise
            </label>

            {form.is_company && (
              <div>
                <Label>Nom de l'entreprise</Label>
                <Input value={form.company} onChange={e => update('company', e.target.value)} placeholder="Ma Société SAS" />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Nom complet *</Label>
                <Input value={form.name} onChange={e => update('name', e.target.value)} data-testid="fret-name" />
              </div>
              <div>
                <Label>Téléphone *</Label>
                <Input type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="+33 6 XX XX XX XX" data-testid="fret-phone" />
              </div>
              <div className="md:col-span-2">
                <Label>Email *</Label>
                <Input type="email" value={form.email} onChange={e => update('email', e.target.value)} data-testid="fret-email" />
              </div>
            </div>

            <div>
              <Label>Notes complémentaires</Label>
              <Textarea value={form.notes} onChange={e => update('notes', e.target.value)} rows={3} placeholder="Informations supplémentaires, questions spécifiques..." />
            </div>

            {/* Summary recap */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm space-y-1">
              <p className="font-semibold text-slate-900 mb-2">Récapitulatif</p>
              <p><strong>Service :</strong> {selectedService?.title || '-'}</p>
              <p><strong>Enlèvement :</strong> {PICKUP_METHODS.find(m => m.id === form.pickup_method)?.title || '-'} {form.pickup_city && `(${form.pickup_city})`}</p>
              <p><strong>Livraison :</strong> {DELIVERY_METHODS.find(m => m.id === form.delivery_method)?.title || '-'} {form.destination_country && `vers ${form.destination_city || form.destination_country}`}</p>
              {form.photos.length > 0 && <p><strong>Photos :</strong> {form.photos.length} jointe(s)</p>}
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(3)}><ArrowLeft className="w-4 h-4 mr-2" /> Retour</Button>
              <Button onClick={submit} disabled={!canSubmit || submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="fret-submit">
                {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi...</>) : (<>Envoyer la demande <CheckCircle2 className="w-4 h-4 ml-2" /></>)}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
